import { useState, useMemo, useCallback, useEffect } from 'react';
import { TranscriptUtterance, wordsToPlainText } from '../types';

export interface SearchMatch {
  utteranceId: string;
  wordId: string;
  matchStartPos: number;
  matchEndPos: number;
}

export interface SearchNavigationState {
  query: string;
  matches: SearchMatch[];
  currentIndex: number;
  isActive: boolean;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  goToNextMatch: () => void;
  goToPrevMatch: () => void;
}

export const useSearchNavigation = (
  utterances: TranscriptUtterance[],
  onNavigateToWord: (utteranceId: string, wordId: string) => void,
  audioFileId?: string
): SearchNavigationState => {
  const [query, setQuery] = useState('');
  const [currentIndex, setCurrentIndex] = useState(-1);

  // Reset search state when audio file changes
  useEffect(() => {
    setQuery('');
    setCurrentIndex(-1);
  }, [audioFileId]);

  // Find all matches in the transcript
  const matches = useMemo(() => {
    // Require at least 2 characters for search
    if (!query.trim() || query.trim().length < 2) return [];

    const searchMatches: SearchMatch[] = [];
    const searchTerm = query.toLowerCase();

    utterances.forEach((utterance) => {
      const text = wordsToPlainText(utterance.transcriptWords).toLowerCase();
      let searchIndex = 0;

      while (searchIndex < text.length) {
        const matchIndex = text.indexOf(searchTerm, searchIndex);
        if (matchIndex === -1) break;

        // Find which word contains this match
        let charPos = 0;
        let wordId = '';

        for (let i = 0; i < utterance.transcriptWords.length; i++) {
          const word = utterance.transcriptWords[i];
          const wordEndPos = charPos + word.text.length;

          if (charPos <= matchIndex && matchIndex < wordEndPos) {
            wordId = word.id;
            break;
          }

          charPos = wordEndPos + 1; // +1 for space
        }

        searchMatches.push({
          utteranceId: utterance.id,
          wordId,
          matchStartPos: matchIndex,
          matchEndPos: matchIndex + searchTerm.length,
        });

        searchIndex = matchIndex + 1;
      }
    });

    return searchMatches;
  }, [query, utterances]);

  // Navigation functions
  const goToNextMatch = useCallback(() => {
    if (matches.length === 0) return;

    const nextIndex = currentIndex >= matches.length - 1 ? 0 : currentIndex + 1;

    // Set found match as current word and scroll
    setCurrentIndex(nextIndex);
    const match = matches[nextIndex];
    onNavigateToWord(match.utteranceId, match.wordId);
  }, [matches, currentIndex, onNavigateToWord]);

  const goToPrevMatch = useCallback(() => {
    if (matches.length === 0) return;

    const prevIndex = currentIndex <= 0 ? matches.length - 1 : currentIndex - 1;

    // Set found match as current word and scroll
    setCurrentIndex(prevIndex);
    const match = matches[prevIndex];
    onNavigateToWord(match.utteranceId, match.wordId);
  }, [matches, currentIndex, onNavigateToWord]);

  const setSearchQuery = useCallback(
    (newQuery: string) => {
      setQuery(newQuery);
      const hasValidQuery = newQuery.trim().length >= 2;

      // Navigate to first match when search is initiated
      if (hasValidQuery && matches.length > 0) {
        setCurrentIndex(0);
        const match = matches[0];
        onNavigateToWord(match.utteranceId, match.wordId);
      }
    },
    [matches, onNavigateToWord]
  );

  const clearSearch = useCallback(() => {
    setQuery('');
    setCurrentIndex(-1);
  }, []);

  return {
    query,
    matches,
    currentIndex,
    isActive: query.trim().length >= 2,
    setSearchQuery,
    clearSearch,
    goToNextMatch,
    goToPrevMatch,
  };
};
